import { Injectable } from "@angular/core";
import { HttpService } from "../../http/http";
import { ChannelRepository } from "../../../domain/channel-repository";
import {
  Channel,
  ChannelInfo,
  ChannelDistribution,
} from "../../../domain/channel";
import { ChannelCodec } from "../../../domain/channel-codec";
import { Subject } from "rxjs";
import { ChannelMapper } from "./channel.mapper";
import { environment } from "app/environments/environment";
import {HttpStatusCode} from "@angular/common/http";
import moment from "moment/moment";

@Injectable()
export class ChannelRepositoryImpl implements ChannelRepository {
  constructor(
    private httpService: HttpService,
    private channelMapper: ChannelMapper,
  ) {}

  private channels: Channel[];

  channelSaved = new Subject<Channel>();

  channelDeleted = new Subject<number>();

  init() {
    return this.httpService.get("channel").then((result) => {
      this.channels = this.channelMapper.mapGetChannelsResult(result);
    });
  }

  refreshCache() {
    return this.init();
  }
  updateLiveBroadcastShowUid(channelId: number, showUid?: string) {
    return this.httpService.post(`Channel/UpdateLiveBroadcastShowUid`, {
      ChannelId: channelId,
      ShowUid: showUid,
    });
  }

  getTenantChannels(tenantId: number = null) {
    if (tenantId == null) {
      return Promise.resolve(this.channels.slice(0)); // todo - ran: change this to deep clone.
    } else {
      return this.httpService
        .get(`channel?tenantIdParam=${tenantId}`)
        .then((result) => this.channelMapper.mapGetChannelsResult(result));
    }
  }

  public generateDownloadChannelRecordingToken(channelId: number,availabilityZoneId: number, fromTimeUtc: moment.Moment, toTimeUtc: moment.Moment): Promise<string> {
    return this.httpService.get(`channel/GenerateDownloadChannelRecordingToken?ChannelId=${channelId}&AvailabilityZoneId=${availabilityZoneId}&date=${fromTimeUtc.format("YYYYMMDD")}&TimeFrom=${fromTimeUtc.format("HHmmssSSS")}&TimeTo=${toTimeUtc.format("HHmmssSSS")}`).then((generatedToken) => {
      return generatedToken;
    });
  }


  public async downloadChannelRecording(token: string,availabilityZoneId: number, fromTimeUtc: moment.Moment, toTimeUtc: moment.Moment): Promise<HttpStatusCode> {
    const downloadUrl = this.httpService.prepareUrl(`channel/DownloadChannelRecording?AvailabilityZoneId=${availabilityZoneId}&date=${fromTimeUtc.format("YYYYMMDD")}&TimeFrom=${fromTimeUtc.format("HHmmssSSS")}&TimeTo=${toTimeUtc.format("HHmmssSSS")}&Token=${token}`);


      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = '';

      a.click();

      return HttpStatusCode.Ok;

  }

  getAllChannels(
    tenantId?: number,
    isActive?: boolean,
    isCreatedReady?: boolean,
    includeDeleted?: boolean,
    isBlock = false,
  ) {
    let url = `channel?allChannels=true`;
    if (!!tenantId) {
      url += `&tenantIdParam=${tenantId}`;
    }
    if (isActive !== undefined) {
      url += `&isActive=${isActive}`;
    }
    if (isCreatedReady !== undefined && isCreatedReady !== null) {
      url += `&isCreatedReady=${isCreatedReady}`;
    }
    if (includeDeleted !== undefined && includeDeleted !== null) {
      url += `&includeDeleted=${includeDeleted}`;
    }
    if (isBlock) {
      url += `&isBlock=${isBlock}`;
    }
    return this.httpService.get(url).then((result) => {
      var channels = this.channelMapper.mapGetChannelsResult(result);
      // Append channels to this.channels if they are not already present
      if (Array.isArray(channels)) {
        channels.forEach((channel) => {
          if (!this.channels.some((c) => c.id === channel.id)) {
            this.channels.push(channel);
          }
        });
      }
      return channels;
    });
  }

  getChannelById(id) {
    let foundChannel: Channel = this.channels.find((x) => x.id === id);
    if (!foundChannel) {
      //probably it's a block

      foundChannel = new Channel(
        id,
        id.toString(),
        null,
        null,
        null,
        false,
        null,
        22,
        null,
        null,
        null,
        null,
        null,
        null,
        true,
        false,
      );
    }
    return Promise.resolve(foundChannel);
  }

  startCreate(
    name: string,
    timeZoneId: number,
    logoX: number,
    logoY: number,
    logoHeight: number,
    logoWidth: number,
    isBlock: boolean,
  ) {
    return this.httpService
      .post(
        "channel/startCreate",
        this.channelMapper.mapStartCreateRequest(
          name,
          timeZoneId,
          logoX,
          logoY,
          logoHeight,
          logoWidth,
          isBlock,
        ),
      )
      .then((result) => {
        return this.channelMapper.mapStartCreateResult(result);
      });
  }

  save(
    channelInfo: ChannelInfo,
    availabilityZoneIdsToDeploy: number[],
    initCueperiodSetting: boolean,
  ) {
    // console.log('before save',channelInfo);
    return this.httpService
      .post(
        "channel/update",
        this.channelMapper.mapSaveRequest(
          channelInfo,
          initCueperiodSetting,
          availabilityZoneIdsToDeploy,
        ),
      )
      .then(() => {
        this.getChannelInfoById(channelInfo.id).then((newChannelInfo) => {
          const index = this.channels.findIndex(
            (x) => x.id === newChannelInfo.id,
          );
          if (index > -1) {
            this.channels.splice(index, 1, newChannelInfo);
          } else {
            this.channels.push(newChannelInfo);
          }
          this.channelSaved.next(newChannelInfo);
        });
      });
  }

  getChannelCodecs(channelId?: number) {
    const uri = !!channelId
      ? `channel/${channelId}/codecs`
      : `channel/generalCodecs`;
    return this.httpService.get(uri).then((result) => {
      return this.channelMapper.mapGetChannelCodecsResult(result);
    });
  }

  addChannelCodec(codec: ChannelCodec, channelId: number) {
    return this.httpService.post(
      `channel/${channelId}/codec`,
      this.channelMapper.mapSetChannelCodecResult(codec),
    );
  }

  updateChannelCodec(codec: ChannelCodec, channelId: number) {
    return this.httpService.put(
      `channel/${channelId}/codec/${codec.id}`,
      this.channelMapper.mapSetChannelCodecResult(codec),
    );
  }

  getChannelDistributionOptions(): Promise<ChannelDistribution> {
    const jsonUrl: string = environment.CODEC_CONFIG_URL;
    return this.httpService.getCodecJson(
      jsonUrl,
      this.httpService.createDefaultNonAuthRequestOption(),
    );
  }

  getChannelInfoById(channelId: number) {
    return this.httpService.get(`channel/${channelId}/info`).then((result) => {
      return this.channelMapper.mapGetChannelInfo(result);
    });
  }

  setChannelActiveState(channelId: number, state: boolean) {
    return this.httpService
      .post(`channel/${channelId}/updateIsActive`, { IsActive: state })
      .then(() => {
        const channel = this.channels.find((x) => x.id === channelId);
        if (channel) {
          channel.isActive = state;
        }
        this.channelSaved.next(channel);
      });
  }

  getChannelCode(channelId: number, expiryDays: number) {
    return this.httpService.get(
      `channel/${channelId}/PreviewCode?expiryDays=${expiryDays}`,
    );
  }

  deleteChannel(channel: Channel) {
    return this.httpService.delete(`Channel/${channel.id}`).then((res) => {
      const index = this.channels.findIndex((x) => x.id === channel.id);
      if (index > -1) {
        this.channels.splice(index, 1);
      }
      this.channelDeleted.next(res);
    });
  }

  checkIfSrtPortIsFree(channelId: number, portNum: number) {
    return this.httpService.get(
      `Channel/CheckIfSrtPortIsFree?channelId=${channelId}&portNum=${portNum}`,
    );
  }

  generateOverrideChannelJSON(
    channelId: number,
    availabilityZoneId: number,
  ): Promise<string> {
    return this.httpService.get(
      `channel/GenerateOverrideChannelJson?channelId=${channelId}&availabilityZoneId=${availabilityZoneId}`,
    );
  }
  updatePlaylistBy(mediaIds: number[], layoutUids: string[]) {
    return this.httpService.post(`Channel/UpdatePlaylistBy`, {
      MediaIds: mediaIds,
      LayoutUids: layoutUids,
    });
  }
}
